import React from 'react';
import { Navigate } from 'react-router-dom';
import useAuthStore from '../stores/authStore';

/**
 * Componente per proteggere le rotte che richiedono autenticazione
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componente da renderizzare se autenticato
 * @param {string} [props.requiredRole] - Ruolo minimo richiesto (superadmin, admin, editor, viewer)
 * @param {string} [props.redirectTo='/login'] - Path di redirect se non autenticato
 */
export default function ProtectedRoute({ 
  children, 
  requiredRole = null,
  redirectTo = '/login' 
}) {
  const { isAuthenticated, hasRole, isLoading } = useAuthStore();

  // Mostra loading durante la verifica dell'autenticazione
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // Redirect a login se non autenticato
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Se è richiesto un ruolo specifico, verifica che l'utente lo abbia
  if (requiredRole && !hasRole(requiredRole)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md text-center">
          <div className="text-6xl mb-4">🚫</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Accesso Negato</h1>
          <p className="text-gray-600">
            Non hai i permessi necessari per accedere a questa sezione.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            Ruolo richiesto: <span className="font-medium">{requiredRole}</span>
          </p>
        </div>
      </div>
    );
  }

  // Tutto ok, renderizza il contenuto protetto
  return children;
}
